import { useEffect } from 'react';
import toast from 'react-hot-toast';
import api from '../../api/axiosInstance';
import { ENDPOINTS } from '../../api/config';
import { useAuth } from '../../contexts/AuthContext';
import { clearCache, invalidateSession } from '../NewDashboard/widgets/widgetDataUtils';

const STORAGE_PREFIX = 'laptura_upload_jobs_';
export const UPLOAD_JOBS_CHANGED = 'laptura:upload-jobs-changed';
export const SESSION_UPLOAD_READY = 'laptura:session-upload-ready';

const storageKey = (userId) => `${STORAGE_PREFIX}${userId}`;

export function readUploadJobs(userId) {
  try {
    const jobs = JSON.parse(localStorage.getItem(storageKey(userId)) || '[]');
    return Array.isArray(jobs) ? jobs : [];
  } catch {
    return [];
  }
}

function writeUploadJobs(userId, jobs) {
  if (jobs.length) localStorage.setItem(storageKey(userId), JSON.stringify(jobs));
  else localStorage.removeItem(storageKey(userId));
}

export function trackUploadJob(userId, job) {
  writeUploadJobs(userId, [...readUploadJobs(userId).filter(({ id }) => id !== job.id), job]);
  window.dispatchEvent(new Event(UPLOAD_JOBS_CHANGED));
}

export function UploadJobMonitor() {
  const { user } = useAuth();

  useEffect(() => {
    if (!user?.id) return undefined;

    let stopped = false;
    let timer;
    const dismissalTimers = new Map();
    const toastId = (job) => job.toastId || `upload-${job.id}`;
    const showTemporaryToast = (type, message, id, duration) => {
      clearTimeout(dismissalTimers.get(id));
      toast[type](message, { id, duration });
      dismissalTimers.set(id, setTimeout(() => {
        dismissalTimers.delete(id);
        toast.dismiss(id);
      }, duration));
    };

    const poll = async () => {
      clearTimeout(timer);
      const jobs = readUploadJobs(user.id);
      if (!jobs.length || stopped) return;

      const remaining = [];
      for (const job of jobs) {
        const label = job.label || job.filename;
        if (job.showHandoff) {
          showTemporaryToast(
            'success',
            `${label} uploaded. You can leave this page. We'll notify you when it's ready.`,
            toastId(job),
            4000,
          );
        }

        try {
          const { data } = await api.get(ENDPOINTS.uploadJob(job.id));
          if (data.status === 'ready') {
            clearCache();
            if (job.kind === 'reprocess') invalidateSession(data.sessionId);
            showTemporaryToast(
              'success',
              job.kind === 'reprocess'
                ? `${label} has been reprocessed.`
                : `${label} is ready to analyze.`,
              toastId(job),
              5000,
            );
            window.dispatchEvent(new CustomEvent(SESSION_UPLOAD_READY, {
              detail: { sessionId: data.sessionId, kind: job.kind || 'upload' },
            }));
          } else if (data.status === 'failed') {
            showTemporaryToast(
              'error',
              data.error || `${label} could not be processed.`,
              toastId(job),
              6000,
            );
          } else {
            remaining.push({ ...job, showHandoff: false });
          }
        } catch (error) {
          if (error?.response?.status === 404) {
            showTemporaryToast(
              'error',
              `Processing status for ${label} is no longer available.`,
              toastId(job),
              6000,
            );
          } else {
            remaining.push({ ...job, showHandoff: false });
          }
        }
      }

      writeUploadJobs(user.id, remaining);
      if (remaining.length && !stopped) timer = setTimeout(poll, 2500);
    };

    const onJobsChanged = () => poll();
    window.addEventListener(UPLOAD_JOBS_CHANGED, onJobsChanged);
    poll();

    return () => {
      stopped = true;
      clearTimeout(timer);
      window.removeEventListener(UPLOAD_JOBS_CHANGED, onJobsChanged);
    };
  }, [user?.id]);

  return null;
}
